/**
 * flowLayout - shared elkjs layout for the BPR React Flow surfaces
 * (BprDiagramCanvas and BprGraphSystemView).
 *
 * One 'layered' configuration so every diagram reads the same way: RIGHT for
 * process/system maps, DOWN for the system-architecture stack. Spacing leaves
 * room for edge labels between layers; model order is preserved so the
 * server-emitted reading order survives into the drawing.
 */

import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

export type FlowDirection = 'RIGHT' | 'DOWN';

export interface FlowLayoutItem {
  id: string;
  width: number;
  height: number;
}

export interface FlowLayoutLink {
  from: string;
  to: string;
}

/** Position each item with elk 'layered'; returns top-left coordinates. */
export async function layoutElkGraph(
  items: FlowLayoutItem[],
  links: FlowLayoutLink[],
  direction: FlowDirection
): Promise<Map<string, { x: number; y: number }>> {
  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,
      'elk.aspectRatio': '1.5',
      'elk.layered.wrapping.strategy': 'MULTI_EDGE',
      // Layer gap holds an edge label; node gap keeps parallel lanes apart.
      'elk.layered.spacing.nodeNodeBetweenLayers': '56',
      'elk.spacing.nodeNode': '36',
      'elk.layered.spacing.edgeNodeBetweenLayers': '24',
      'elk.spacing.edgeNode': '24',
      'elk.spacing.edgeEdge': '16',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    },
    children: items.map((item) => ({ id: item.id, width: item.width, height: item.height })),
    edges: links.map((link, i) => ({ id: `e${i}`, sources: [link.from], targets: [link.to] })),
  };
  const laid = await elk.layout(graph);
  const positions = new Map<string, { x: number; y: number }>();
  for (const child of laid.children ?? []) {
    positions.set(child.id, { x: child.x ?? 0, y: child.y ?? 0 });
  }
  return positions;
}
